import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';

import { useEnableHsCodePreFilled } from '../../../../feature-flags/hooks/use-enable-hs-code-pre-filled';
import { useEnableV3CustomsWeightField } from '../../../../feature-flags/hooks/use-enable-v3-customs-weight-field';
import { useInternationalRequirements } from '../../../../hooks/use-international-requirements';
import { useCustomsContext } from '../../context/context';
import Global from '../../../../store/global-selectors';
import { CustomsDeclaredValue } from '../declared-value-input';
import { CustomsDescription } from '../description-input';
import { CustomsHarmonizationCode } from '../harmonization-code-input';
import { CustomsCountryOfOrigin } from '../origin-country-dropdown';
import { CustomsQuantity } from '../quantity-input';
import { CustomsSku } from '../sku-input';
import { CustomsTotal } from '../total-value-display';
import { CustomsWeight } from '../weight-input';
import { CustomsMid } from '../mid-input';

import type { CustomsItem } from '../../types';
import type { Currency } from '../../../../prop-types/currency-types';
import type { WeightValue } from '../../../../prop-types/weight-types';
import { WeightUnit } from '../../../../enums/weight-unit';
import { Unknown } from '../../../../constants/currency-codes';

import style from './customs-declaration.module.less';
import messages from '../messages';

type CustomDeclarationProps = {
  customItem: CustomsItem;
  index?: number;
  onRemoveDeclaration?: (index: number) => void;
};

const defaultWeightValue: WeightValue = { value: 0, unit: WeightUnit.Unknown };
const defaultUnitPrice = { value: 0, code: Unknown };
const HS_CODE_DEFAULT_ATTRIBUTE_KEY = 'hc_default';

export const CustomDeclaration = ({ customItem, index = 0, onRemoveDeclaration }: CustomDeclarationProps) => {
  const {
    description = '',
    weight = defaultWeightValue,
    sku = '',
    quantity = 0,
    unitPrice = defaultUnitPrice,
    harmonizedTariffCode = '',
    originCountryCode = '',
    declarationKey,
    mid = '',
    fulfillmentPlanItemId = '',
  } = customItem;
  const { removeDeclaration, updateDeclaration, fulfillmentPlanId } = useCustomsContext();

  const isHsCodePreFilledEnabled = useEnableHsCodePreFilled();
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const matchedFpItem = isHsCodePreFilledEnabled
    ? fulfillmentPlan?.items?.find((item: { fulfillmentPlanItemId: string }) => item.fulfillmentPlanItemId === fulfillmentPlanItemId)
    : undefined;
  const hsCodeCountryAttributeKey = originCountryCode ? `hc_${originCountryCode.toUpperCase()}` : null;

  const isHsCodePreFilled =
    isHsCodePreFilledEnabled &&
    !!fulfillmentPlanItemId &&
    !!harmonizedTariffCode &&
    !!matchedFpItem?.attributes?.some((attr: { name: string }) => attr.name === HS_CODE_DEFAULT_ATTRIBUTE_KEY) &&
    !matchedFpItem?.attributes?.some((attr: { name: string }) => attr.name === hsCodeCountryAttributeKey);

  const enableV3CustomsWeightField = useEnableV3CustomsWeightField();
  const { applyForTariffs } = useInternationalRequirements();

  const remove = useCallback(() => {
    if (declarationKey) {
      removeDeclaration(declarationKey);
      onRemoveDeclaration?.(index);
    }
  }, [declarationKey, index, onRemoveDeclaration, removeDeclaration]);

  const update = (updatedPayload: Partial<CustomsItem>) => {
    if (declarationKey) {
      updateDeclaration(declarationKey, updatedPayload);
    }
  };

  return (
    <FlexColumn key={declarationKey} className={style['declaration-wrapper']}>
      <FlexRow className={style['declaration-header-wrapper']}>
        <Text formattedMessageValues={{ declaredCount: `${index + 1}` }} textType={TextType.H4} value={messages.declarationHeader} />
        <Button className='remove-declaration-button' onClick={remove} theme='danger-link'>
          <FormattedMessage {...messages.delete} />
        </Button>
      </FlexRow>

      <FlexColumn gap={Gap.Space8}>
        <CustomsDescription onChange={(newDescription: string) => update({ description: newDescription })} value={description} />
        {enableV3CustomsWeightField && (
          <CustomsWeight onChange={(newWeight: WeightValue) => update({ weight: newWeight })} value={weight} />
        )}

        <CustomsSku onChange={(newSku: string | number) => update({ sku: String(newSku) })} value={sku} />

        {applyForTariffs ? <CustomsMid onChange={(newMid: string | number) => update({ mid: String(newMid) })} value={mid} /> : null}

        <CustomsQuantity onChange={(newQuantity: number) => update({ quantity: newQuantity })} value={quantity} />

        <CustomsDeclaredValue
          declaredAmount={unitPrice}
          onChange={(value: number, code: string) => update({ unitPrice: { value, code } as Currency })}
        />

        <CustomsTotal declaredAmount={unitPrice} quantity={quantity} />

        <CustomsHarmonizationCode
          description={description}
          isPrefilled={isHsCodePreFilled}
          onChange={(newCode: string) => update({ harmonizedTariffCode: newCode })}
          value={harmonizedTariffCode}
        />

        <CustomsCountryOfOrigin
          onChange={(newCountry: string) => update({ originCountryCode: newCountry })}
          selectedCountryCode={originCountryCode}
        />
      </FlexColumn>
    </FlexColumn>
  );
};
